import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';
import {
  Stars as StarsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import loyaltyProgramService from '../../services/loyaltyProgramService';

interface CustomerLoyaltyPointsProps {
  contactId: string;
}

interface LoyaltyTransaction {
  _id: string;
  type: 'earned' | 'redeemed' | 'returned' | 'expired' | 'adjusted';
  points: number;
  balance: number;
  source: string;
  invoiceId?: {
    _id: string;
    invoiceNumber: string;
    total: number;
    createdAt: string;
  };
  loyaltyProgramId?: {
    _id: string;
    name: string;
  };
  notes?: string;
  expiryDate?: string;
  createdAt: string;
}

interface Statistics {
  totalEarned: number;
  totalRedeemed: number;
  totalReturned: number;
  totalExpired: number;
  currentBalance: number;
  pointsValue: number;
  transactionCount: number;
}

const CustomerLoyaltyPoints: React.FC<CustomerLoyaltyPointsProps> = ({ contactId }) => {
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    totalEarned: 0,
    totalRedeemed: 0,
    totalReturned: 0,
    totalExpired: 0,
    currentBalance: 0,
    pointsValue: 0,
    transactionCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  // حالة النموذج
  const [openDialog, setOpenDialog] = useState(false);
  const [adjustType, setAdjustType] = useState<'add' | 'deduct'>('add');
  const [adjustPoints, setAdjustPoints] = useState<string>('');
  const [adjustExpiryDate, setAdjustExpiryDate] = useState<string>('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [adjustSuccess, setAdjustSuccess] = useState<string | null>(null);

  // جلب البيانات من API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit,
        type: filterType !== 'all' ? filterType : undefined,
        search: searchTerm || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      };

      const response = await loyaltyProgramService.getCustomerLoyaltyPoints(contactId, params);

      if (response.success) {
        setTransactions(response.data.transactions);
        setStatistics(response.data.statistics);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err: any) {
      console.error('Error fetching loyalty points:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [contactId, page, filterType, dateFrom, dateTo]);

  // البحث عند الضغط على Enter
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchData();
    }
  };

  const handleReset = () => {
    setFilterType('all');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    fetchData();
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ar });
  };

  // فتح النموذج
  const handleOpenDialog = () => {
    setOpenDialog(true);
    setAdjustType('add');
    setAdjustPoints('');
    setAdjustExpiryDate('');
    setAdjustNotes('');
    setAdjustError(null);
    setAdjustSuccess(null);
  };

  // إغلاق النموذج
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAdjustType('add');
    setAdjustPoints('');
    setAdjustExpiryDate('');
    setAdjustNotes('');
    setAdjustError(null);
    setAdjustSuccess(null);
  };

  // تنفيذ تعديل النقاط
  const handleAdjustPoints = async () => {
    try {
      setAdjustLoading(true);
      setAdjustError(null);
      setAdjustSuccess(null);

      const points = parseInt(adjustPoints);
      if (isNaN(points) || points <= 0) {
        setAdjustError('يرجى إدخال عدد نقاط صحيح وموجب');
        return;
      }

      if (adjustType === 'deduct' && points > statistics.currentBalance) {
        setAdjustError(`رصيد العميل الحالي (${statistics.currentBalance}) غير كافٍ لخصم ${points} نقطة`);
        return;
      }

      const response = await loyaltyProgramService.adjustCustomerPoints({
        contactId,
        points,
        type: adjustType,
        expiryDate: adjustType === 'add' && adjustExpiryDate ? adjustExpiryDate : undefined,
        notes: adjustNotes
      });

      if (response.success) {
        setAdjustSuccess(response.message);
        // إعادة تحميل البيانات
        setTimeout(() => {
          handleCloseDialog();
          fetchData();
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error adjusting points:', err);
      setAdjustError(err.response?.data?.message || 'حدث خطأ أثناء تعديل النقاط');
    } finally {
      setAdjustLoading(false);
    }
  };

  return (
    <Box>
      {/* زر تعديل النقاط */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            }
          }}
        >
          تعديل النقاط
        </Button>
      </Box>

      {/* بطاقات الإحصائيات */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* مربع قيمة النقاط بالريال */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    قيمة النقاط بالريال
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.pointsValue.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    ريال سعودي
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* مربع النقاط المسترجعة */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    النقاط المسترجعة
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalReturned}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    الرصيد الحالي
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.currentBalance}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <StarsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#333'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontWeight: 'bold' }}>
                    إجمالي النقاط المكتسبة
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalEarned}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    إجمالي النقاط المستبدلة
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalRedeemed}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    النقاط المنتهية
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalExpired}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* الفلاتر */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            تصفية النتائج
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع الحركة</InputLabel>
                <Select
                  value={filterType}
                  label="نوع الحركة"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="earned">نقاط مكتسبة</MenuItem>
                  <MenuItem value="redeemed">نقاط مستبدلة</MenuItem>
                  <MenuItem value="returned">نقاط مسترجعة</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="بحث"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="رقم الفاتورة، المصدر..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="من تاريخ"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="إلى تاريخ"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ height: '40px' }}
              >
                إعادة تعيين
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* الجدول */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            سجل نقاط الولاء
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : transactions.length === 0 ? (
            <Alert severity="info">
              لا توجد حركات نقاط ولاء لهذا العميل
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>النوع</TableCell>
                    <TableCell align="center">النقاط</TableCell>
                    <TableCell>المصدر</TableCell>
                    <TableCell>رقم الفاتورة</TableCell>
                    <TableCell align="center">تاريخ الانتهاء</TableCell>
                    <TableCell align="center">الرصيد</TableCell>
                    <TableCell>ملاحظات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction._id} hover>
                      <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            transaction.type === 'earned' ? 'مكتسبة' :
                            transaction.type === 'redeemed' ? 'مستبدلة' :
                            transaction.type === 'returned' ? 'مسترجعة' :
                            transaction.type === 'expired' ? 'منتهية' : 'معدلة'
                          }
                          color={
                            transaction.type === 'earned' ? 'success' :
                            transaction.type === 'redeemed' ? 'warning' :
                            transaction.type === 'returned' ? 'error' :
                            transaction.type === 'expired' ? 'error' : 'default'
                          }
                          size="small"
                          icon={transaction.type === 'earned' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: transaction.type === 'earned' ? 'success.main' :
                                   transaction.type === 'returned' ? 'error.main' : 'warning.main'
                          }}
                        >
                          {transaction.type === 'earned' ? '+' : '-'}{transaction.points}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.source}</TableCell>
                      <TableCell>
                        {transaction.invoiceId ? (
                          <Chip label={transaction.invoiceId.invoiceNumber} size="small" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {transaction.expiryDate ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: new Date(transaction.expiryDate) < new Date() ? 'error.main' : 'text.primary',
                              fontWeight: new Date(transaction.expiryDate) < new Date() ? 'bold' : 'normal'
                            }}
                          >
                            {formatDate(transaction.expiryDate)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 'bold' }}>
                          {transaction.balance}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {transaction.notes || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
          )}
        </CardContent>
      </Card>

      {/* نموذج تعديل النقاط */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          تعديل نقاط الولاء
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {adjustError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {adjustError}
            </Alert>
          )}
          {adjustSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {adjustSuccess}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
              نوع العملية
            </FormLabel>
            <RadioGroup
              value={adjustType}
              onChange={(e) => setAdjustType(e.target.value as 'add' | 'deduct')}
              row
            >
              <FormControlLabel
                value="add"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon color="success" />
                    <Typography>إضافة نقاط</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="deduct"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon color="error" />
                    <Typography>خصم نقاط</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          <TextField
            fullWidth
            label="عدد النقاط"
            type="number"
            value={adjustPoints}
            onChange={(e) => setAdjustPoints(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
            helperText={`الرصيد الحالي: ${statistics.currentBalance} نقطة`}
          />

          {adjustType === 'add' && (
            <TextField
              fullWidth
              label="تاريخ الانتهاء (اختياري)"
              type="date"
              value={adjustExpiryDate}
              onChange={(e) => setAdjustExpiryDate(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText="حدد تاريخ انتهاء صلاحية النقاط (اتركه فارغاً إذا لم تكن النقاط تنتهي)"
            />
          )}

          <TextField
            fullWidth
            label="ملاحظات (اختياري)"
            multiline
            rows={3}
            value={adjustNotes}
            onChange={(e) => setAdjustNotes(e.target.value)}
            placeholder="أدخل سبب التعديل أو أي ملاحظات..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={adjustLoading}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleAdjustPoints}
            variant="contained"
            disabled={adjustLoading || !adjustPoints}
            startIcon={adjustType === 'add' ? <AddIcon /> : <RemoveIcon />}
            sx={{
              background: adjustType === 'add'
                ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              '&:hover': {
                background: adjustType === 'add'
                  ? 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)'
                  : 'linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%)',
              }
            }}
          >
            {adjustLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              adjustType === 'add' ? 'إضافة النقاط' : 'خصم النقاط'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerLoyaltyPoints;